<template>
  <div class="form-property-information">
    <bg-skeleton v-if="loadingFetchData" width="40%" :height="40" />
    <template v-else>
      <div class="form-property-information__header-section">
        <bg-text size="heading-2">Data Properti</bg-text>
      </div>
      <div class="form-property-information__form-section">
        <property-keeper-section
          :keepers="keeperData"
          :role-options="keeperRoleOptions"
          class="bg-u-mb-xxl"
          @add="handleAddList"
          @delete="handleDeleteList"
        />

        <!-- Survey Time -->
        <div class="form-property-information__category">
          <bg-text size="heading-4" class="bg-u-mb-xxs"> Waktu Survei </bg-text>

          <bg-text size="body-2" class="bg-u-mb-lg">
            Atur hari dan jam survei yang tersedia. Jika tidak tersedia di hari
            tertentu, kosongkan jam mulai dan jam selesai survei.
          </bg-text>

          <bg-grid class="form-property-information__survey-time">
            <bg-grid-item :col="6">
              <bg-grid
                v-for="(day, index) in SURVEY_DAYS"
                :key="day.value"
                vAlign="center"
              >
                <bg-grid-item>
                  <bg-text
                    size="title-5"
                    :class="{ 'bg-u-mt-xxl': index === 0 }"
                  >
                    {{ day.label }}
                  </bg-text>
                </bg-grid-item>

                <bg-grid-item>
                  <bg-text
                    v-if="index === 0"
                    size="title-5"
                    class="bg-u-ml-lg bg-u-mb-xs"
                  >
                    Jam Mulai Survei
                  </bg-text>

                  <bg-input
                    v-model="detailData.survey_time[index].start"
                    type="time"
                  />
                </bg-grid-item>

                <bg-grid-item :col="1">
                  <bg-text
                    size="title-5"
                    :class="{ 'bg-u-mt-xxl': index === 0 }"
                  >
                    s/d
                  </bg-text>
                </bg-grid-item>

                <bg-grid-item>
                  <bg-text
                    v-if="index === 0"
                    size="title-5"
                    class="bg-u-ml-md bg-u-mb-xs"
                  >
                    Jam Selesai Survei
                  </bg-text>

                  <bg-input
                    v-model="detailData.survey_time[index].end"
                    type="time"
                  />
                </bg-grid-item>
              </bg-grid>
            </bg-grid-item>
          </bg-grid>
        </div>
        <!-- End Survey Time -->

        <div class="form-property-strategic-location">
          <div class="form-property-strategic-location__header">
            <bg-text
              size="heading-4"
              class="form-property-strategic-location-title"
            >
              Lokasi Strategis
            </bg-text>
            <bg-button
              variant="secondary"
              :disabled="strategicLocation.length >= 10"
              @click="strategicLocation.push({ location: '' })"
            >
              + Tambah Lokasi Strategis
            </bg-button>
          </div>
          <div v-for="(item, index) in strategicLocation" :key="index">
            <bg-field
              :error="item.location.length > 255"
              :message="
                item.location.length > 255
                  ? 'Panjang karakter maksimal adalah 255 karakter.'
                  : ''
              "
            >
              <div class="form-property-strategic-location__input">
                <bg-input
                  v-model="item.location"
                  placeholder="Lokasi Strategis"
                />

                <bg-button-icon
                  icon="delete"
                  variant="tertiary-naked"
                  :disabled="strategicLocation.length === 1"
                  @click="handleDeleteStrategicLocation('confirmation', index)"
                />
              </div>
            </bg-field>
          </div>
        </div>
        <div class="form-property-information__category">
          <bg-text
            size="heading-4"
            class="form-property-information__category-title"
            >Fasilitas Bersama Kos</bg-text
          >
          <multiple-input
            v-if="!loadingFetchTagList"
            :suggestions="sharedTags"
            @input="handleSharedInput"
            :value="detailData.shared_facilities"
          />
        </div>
        <div class="form-property-information__category">
          <bg-grid>
            <bg-grid-item>
              <bg-field label="Provider Wifi">
                <bg-input
                  v-model="detailData.wifi_provider"
                  :disabled="disableWifiField"
                />
              </bg-field>
            </bg-grid-item>
            <bg-grid-item>
              <bg-field label="Kecepatan Wifi">
                <bg-input
                  v-model="detailData.wifi_speed"
                  :disabled="disableWifiField"
                />
              </bg-field>
            </bg-grid-item>
          </bg-grid>
        </div>

        <div class="form-property-information__category">
          <bg-text
            size="heading-4"
            class="form-property-information__category-title"
            >Fasilitas Parkir</bg-text
          >
          <bg-grid>
            <bg-grid-item :col="6">
              <bg-grid
                v-for="(item, i) in detailData.parking_facilities"
                :key="i"
                vAlign="center"
              >
                <bg-grid-item :col="6">
                  <bg-checkbox
                    :id="item.parking_type"
                    :label="item.parking_type"
                    v-model="parkingValue"
                    :value="parkingType[item.parking_type]"
                  />
                </bg-grid-item>
                <bg-grid-item :col="6">
                  <bg-input
                    v-model="item.total_quota"
                    :disabled="
                      !parkingValue.includes(parkingType[item.parking_type])
                    "
                    :error="
                      parkingValue.includes(parkingType[item.parking_type]) &&
                      !item.total_quota
                    "
                  />
                  <bg-text
                    v-if="
                      parkingValue.includes(parkingType[item.parking_type]) &&
                      !item.total_quota
                    "
                    size="body-4"
                    class="form-property-information__error"
                  >
                    Masukkan kapasitas {{ item.parking_type }} terlebih dahulu.
                  </bg-text>
                </bg-grid-item>
              </bg-grid>
            </bg-grid-item>
          </bg-grid>
        </div>

        <div class="form-property-information__category">
          <bg-text
            size="heading-4"
            class="form-property-information__category-title"
            >Info Tambahan</bg-text
          >
          <multiple-input
            v-if="!loadingFetchTagList"
            :suggestions="additionalTags"
            @input="handleAdditionalInput"
            :value="filteredAdditionalData"
          />
        </div>
        <div class="form-property-information__category">
          <bg-grid>
            <bg-grid-item>
              <bg-field label="Jarak Kos dari Jalan Utama">
                <bg-input v-model="detailData.mainroad_distance" />
              </bg-field>
            </bg-grid-item>
            <bg-grid-item>
              <bg-field
                label="Jam Buka Portal"
                :message="inputMessage.portalOpen"
                :error="!!inputMessage.portalOpen"
              >
                <bg-input v-model="detailData.portal_open_time" type="time" />
              </bg-field>
            </bg-grid-item>
            <bg-grid-item>
              <bg-field
                label="Jam Tutup Portal"
                :message="inputMessage.portalClose"
                :error="!!inputMessage.portalClose"
              >
                <bg-input v-model="detailData.portal_close_time" type="time" />
              </bg-field>
            </bg-grid-item>
          </bg-grid>
        </div>

        <bg-button
          variant="primary"
          :disabled="isSimpanBtnDisabled"
          @click="handleShowConfirmModal"
        >
          Simpan
        </bg-button>

        <bg-modal
          v-model="showDeleteStrategicLocationModal"
          title="Yakin menghapus informasi lokasi strategis?"
          description="Informasi mengenai lokasi strategis ini akan dihapus dan tidak dapat dikembalikan."
          desktop-size="sm"
          :close-on-click-backdrop="false"
          button-main-text="Hapus"
          button-second-text="Batal"
          @click-main-action="handleDeleteStrategicLocation('delete')"
          @click-second-action="showDeleteStrategicLocationModal = false"
        />

        <bg-modal
          v-model="isShowConfirmModal"
          title="Yakin menyimpan perubahan?"
          description="Perubahan pada data properti yang dilakukan akan diterapkan langsung ke iklan kos."
        >
          <template #footer>
            <div class="form-property-information__modal-footer">
              <bg-button
                class="form-property-information__modal-footer-btn"
                variant="tertiary"
                :loading="loadingButton"
                @click="handleShowConfirmModal"
                >Batal</bg-button
              >
              <bg-button
                variant="primary"
                :loading="loadingButton"
                @click="handleSaveData"
                >Simpan</bg-button
              >
            </div>
          </template>
        </bg-modal>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BgSkeleton,
  BgText,
  BgField,
  BgInput,
  BgGrid,
  BgGridItem,
  BgCheckbox,
  BgButton,
  BgButtonIcon,
  BgModal,
} from 'bangul-vue';
import formsPropertyInformationApi from '@admin_endpoints/forms-property-information';
import propertyDetailApi from '@admin_endpoints/property-detail';
import MultipleInput from '@admin_molecules/MultipleInput';
import { mapMutations } from 'vuex';
import PropertyKeeperSection from './components/PropertyKeeperSection';

const INITIAL_DETAIL_DATA = {
  shared_facilities: [],
  wifi_provider: '',
  wifi_speed: '',
  parking_facilities: [],
  additional_infos: [],
  portal_open_time: '',
  portal_close_time: '',
  mainroad_distance: '',
  strategic_location: [],
  survey_time: [
    { day: 1, start: '', end: '' },
    { day: 2, start: '', end: '' },
    { day: 3, start: '', end: '' },
    { day: 4, start: '', end: '' },
    { day: 5, start: '', end: '' },
    { day: 6, start: '', end: '' },
    { day: 7, start: '', end: '' },
  ],
  property_keepers: [],
};

const SURVEY_DAYS = [
  { label: 'Senin', value: 1 },
  { label: 'Selasa', value: 2 },
  { label: 'Rabu', value: 3 },
  { label: 'Kamis', value: 4 },
  { label: 'Jumat', value: 5 },
  { label: 'Sabtu', value: 6 },
  { label: 'Minggu', value: 7 },
];

export default {
  name: 'FormPropertyInformation',

  components: {
    BgSkeleton,
    BgText,
    BgField,
    BgInput,
    BgGrid,
    BgGridItem,
    BgCheckbox,
    BgButton,
    BgButtonIcon,
    MultipleInput,
    BgModal,
    PropertyKeeperSection,
  },

  computed: {
    propertyId() {
      return this.$route.params.propertyId;
    },
    sharedTags() {
      const tagsList = this.sharedSuggestion?.map(item => ({
        ...item,
        isDisable: false,
        label: item.name,
      }));

      return tagsList;
    },
    additionalTags() {
      const tagsList = this.additionalSuggestion?.map(item => ({
        ...item,
        isDisable: false,
        label: item.name,
      }));

      return tagsList;
    },
    sharedCurrent() {
      const tagsList = this.detailData?.shared_facilities?.map(item => ({
        ...item,
        isDisable: false,
        label: item.name,
      }));

      return tagsList;
    },
    propertyName() {
      return localStorage.getItem('propertyName');
    },
    isSimpanBtnDisabled() {
      const isStrategicLocationValid = this.strategicLocation.filter(
        item => item.location.length > 255
      );

      return !!isStrategicLocationValid.length;
    },
  },

  created() {
    this.handleBreadcrumb();
    this.consumePropertyDetail();
    this.consumeTagList();
  },

  data() {
    return {
      SURVEY_DAYS,

      keeperRoleOptions: [
        { val: 'owner', label: 'Pemilik kos' },
        { val: 'keeper', label: 'Penjaga kos' },
      ],
      sendKeeperData: [],
      keeperData: [],
      strategicLocation: [{ location: '' }],
      oldKeeperData: [],
      newKeeperData: [],
      detailData: { ...INITIAL_DETAIL_DATA },
      loadingFetchData: false,
      loadingFetchTagList: false,
      parkingValue: [],
      parkingType: {
        'Parkir Mobil': 'car',
        'Parkir Motor & Sepeda': 'motorcycle',
        'Parkir Motor': 'motorcycle',
        'Parkir Sepeda': 'bike',
      },
      parkingOptions: [
        {
          parking_type: 'Parkir Mobil',
          total_quota: 0,
        },
        {
          parking_type: 'Parkir Motor',
          total_quota: 0,
        },
        {
          parking_type: 'Parkir Sepeda',
          total_quota: 0,
        },
      ],
      sharedSuggestion: [],
      additionalSuggestion: [],
      sharedList: [],
      additionalList: [],
      filteredAdditionalData: [],
      isShowConfirmModal: false,
      loadingButton: false,
      disableWifiField: false,
      inputMessage: {
        surveyFrom: '',
        surveyTo: '',
        portalOpen: '',
        portalClose: '',
        keeper: false,
        parking: false,
      },
      showDeleteStrategicLocationModal: false,
      toBeDeletedStrategicLocation: null,
    };
  },

  methods: {
    ...mapMutations('breadcrumb', ['updateBreadcrumb']),

    handleBreadcrumb() {
      this.updateBreadcrumb({
        index: 1,
        item: {
          name: this.propertyName,
          url: `/property-detail/${this.propertyId}/kos`,
        },
      });
    },

    handleAddList() {
      const addition = {
        operation: 'add',
        role: '',
        name: '',
        phone_number: '',
        notification_note: '',
      };
      this.keeperData.push(addition);
      this.sendKeeperData.push(addition);
    },

    handleDeleteList(index, data) {
      if (index === 0) return;

      this.keeperData.splice(index, 1);
      if (data.id) {
        const deletion = {
          operation: 'delete',
          keeper: {
            id: data.id,
            name: data.name,
            phone_number: data.phone_number,
          },
        };

        this.sendKeeperData.push(deletion);
      } else {
        this.sendKeeperData.splice(index, 1);
      }
    },

    handleUpdateKeeper() {
      let updatedData = this.oldKeeperData.filter(
        item => !(item.operation === 'add' || item.operation === 'deletion')
      );
      let addedData = this.sendKeeperData.filter(
        item => item.operation === 'add'
      );
      const deletedData = this.sendKeeperData.filter(
        item => item.operation === 'delete'
      );
      updatedData = updatedData.map(item => ({
        operation: 'update',
        keeper: {
          id: item.id,
          name: item.name,
          phone_number: item.phone_number,
          role: item.role,
          notification_note: item.notification_note,
        },
      }));

      addedData = addedData.map(item => ({
        operation: 'add',
        keeper: {
          name: item.name,
          phone_number: item.phone_number,
          role: item.role,
          notification_note: item.notification_note,
        },
      }));

      this.newKeeperData = [...updatedData, ...addedData, ...deletedData];

      this.postPropertyKeeper();
    },
    getTimeErrorMessage({ start, end, messages }) {
      let startMessage = '';
      let endMessage = '';

      if (start && !end) {
        endMessage = messages.endEmpty;
      } else if (!start && end) {
        startMessage = messages.startEmpty;
      } else if (start && end) {
        if (start === end) {
          startMessage = messages.startSameAsEnd;
        } else if (start > end) {
          startMessage = messages.startAfterEnd;
        }
      }

      return { startMessage, endMessage };
    },

    handleShowConfirmModal() {
      const {
        portal_open_time: portalOpenTime,
        portal_close_time: portalCloseTime,
      } = this.detailData;

      const emptyCheckedParking = this.detailData.parking_facilities.filter(
        item =>
          this.parkingValue.includes(this.parkingType[item.parking_type]) &&
          !item.total_quota
      );
      const keeperPhoneEmpty = this.keeperData.filter(
        data => data.name && !data.phone_number
      );
      const keeperNameEmpty = this.keeperData.filter(
        data => !data.name && data.phone_number
      );

      const keeper = emptyCheckedParking.length > 0;
      const parking = keeperPhoneEmpty.length > 0 || keeperNameEmpty.length > 0;

      const {
        startMessage: portalOpen,
        endMessage: portalClose,
      } = this.getTimeErrorMessage({
        start: portalOpenTime,
        end: portalCloseTime,
        messages: {
          startEmpty: 'Masukkan jam portal dibuka.',
          endEmpty: 'Masukkan jam portal ditutup.',
          startAfterEnd: 'Jam buka portal harus kurang dari jam tutup portal.',
          startSameAsEnd:
            'Jam buka portal harus berbeda dengan jam tutup portal.',
        },
      });

      this.inputMessage = {
        portalOpen,
        portalClose,
        keeper,
        parking,
      };

      if (
        Object.values(this.inputMessage).every(
          value => !value || value === '' || value === undefined
        )
      ) {
        this.loadingButton = false;
        this.isShowConfirmModal = !this.isShowConfirmModal;
      }
    },

    handleDeleteStrategicLocation(type, index) {
      if (type === 'confirmation') {
        this.toBeDeletedStrategicLocation = index;
        this.showDeleteStrategicLocationModal = true;
      } else if (type === 'delete') {
        this.strategicLocation.splice(this.toBeDeletedStrategicLocation, 1);
        this.toBeDeletedStrategicLocation = null;
        this.showDeleteStrategicLocationModal = false;
      }
    },

    async handleSaveData() {
      this.loadingButton = true;

      const parkingData = this.detailData.parking_facilities.map(item => ({
        type: this.parkingType[item.parking_type],
        quota: item.total_quota ? item.total_quota : 0,
      }));
      const sendData = {
        survey_time: this.detailData.survey_time.filter(
          item => item.start && item.end
        ),
        shared_facilities: this.sharedList,
        parking_facilities: parkingData,
        additional_infos: this.additionalList,
        wifi_provider: this.detailData.wifi_provider,
        wifi_speed: this.detailData.wifi_speed,
        mainroad_distance: this.detailData.mainroad_distance,
        portal_open_time: this.detailData.portal_open_time,
        portal_close_time: this.detailData.portal_close_time,
      };

      await this.updateStrategicLocation({
        locations: this.strategicLocation
          .filter(item => item.location.length)
          .map(item => item.location),
      });

      await this.handleUpdateKeeper();

      await this.updatePropertyInformation(sendData);

      this.loadingButton = false;
      this.isShowConfirmModal = false;
    },

    handleSharedInput(input) {
      const listId = input.map(({ id }) => id);
      const listName = input.map(({ name }) => name);
      this.sharedList = [...new Set(listId)];
      this.disableWifiField = !listName.includes('WiFi');
    },

    handleAdditionalInput(input) {
      const list = input.map(({ id }) => id);
      this.additionalList = [...new Set(list)];
    },

    async postPropertyKeeper() {
      this.loadingFetchData = true;
      let result = {};
      try {
        result = await formsPropertyInformationApi.createBulkPropertyKeeper({
          propertyId: this.propertyId,
          data: this.newKeeperData,
        });
      } catch (error) {
        result.hasError = true;
        console.error(error);
        this.$toast.show(error.response.data.error);
      }
      this.loadingFetchData = false;
      return result;
    },

    async updateStrategicLocation(data) {
      try {
        const response = await formsPropertyInformationApi.updateStrategicLocation(
          {
            propertyId: this.propertyId,
            data,
          }
        );

        if (response) {
          this.$toast.show('Perubahan berhasil disimpan');
        }
      } catch (error) {
        this.$toast.show(error.response.data.error || 'Terjadi kesalahan');
      }
    },

    async updatePropertyInformation(data) {
      this.loadingFetchData = true;
      let result = {};
      try {
        result = await formsPropertyInformationApi.updatePropertyInformation({
          propertyId: this.propertyId,
          data,
        });
        this.$toast.show('Perubahan berhasil disimpan');
        this.handleShowConfirmModal();
        window.location.reload();
      } catch (error) {
        result.hasError = true;
        console.error(error);
        this.$toast.show(error.response.data.error);
      }
      this.loadingFetchData = false;
      return result;
    },

    async fetchPropertyDetail() {
      let result = {};
      try {
        result = await propertyDetailApi.getKosRulesAndInfos(this.propertyId);
      } catch (error) {
        result.hasError = true;
        this.$toast.show(result);
        console.error(error);
      }
      return result;
    },

    async consumePropertyDetail() {
      this.loadingFetchData = true;
      const response = await this.fetchPropertyDetail();

      if (!response.hasError) {
        const { strategic_location } = response?.data?.data || [];

        const rawSurveyTime = response?.data?.data?.survey_time;
        const apiSurveyTime = Array.isArray(rawSurveyTime) ? rawSurveyTime : [];
        const mergedSurveyTime = INITIAL_DETAIL_DATA.survey_time.map(
          defaultDay => {
            const found = apiSurveyTime.find(
              item => item.day === defaultDay.day
            );
            return found ? { ...defaultDay, ...found } : { ...defaultDay };
          }
        );

        this.detailData = {
          ...response?.data?.data,
          survey_time: mergedSurveyTime,
        };

        const propertyKeepers = (
          response?.data?.data?.property_keepers || []
        ).map(keeper => ({
          role: '',
          name: '',
          phone_number: '',
          notification_note: '',
          ...keeper,
        }));

        this.keeperData = propertyKeepers;
        this.oldKeeperData = propertyKeepers;
        if (strategic_location.length) {
          this.strategicLocation = response.data.data.strategic_location.map(
            item => ({ location: item })
          );
        }
        this.filteredAdditionalData = this.detailData.additional_infos.filter(
          info => info.id
        );

        this.handleAdditionalInput(this.filteredAdditionalData);
        this.handleSharedInput(this.detailData.shared_facilities);

        if (this.detailData.parking_facilities.length === 0) {
          this.detailData.parking_facilities = this.parkingOptions;
        }
      }

      this.loadingFetchData = false;
    },

    async fetchTagList(type) {
      let result = {};
      try {
        result = await formsPropertyInformationApi.getPropertyTagList(type);
      } catch (error) {
        result.hasError = true;
        this.$toast.show(result);
        console.error(error);
      }
      return result;
    },

    async consumeTagList() {
      this.loadingFetchTagList = true;

      const responseShared = await this.fetchTagList('shared');
      const responseAdditional = await this.fetchTagList('outside');

      if (responseShared.status) {
        this.sharedSuggestion = responseShared?.data?.tags;
      }

      if (responseAdditional.status) {
        this.additionalSuggestion = responseAdditional?.data?.tags;
      }
      this.loadingFetchTagList = false;
    },
  },
};
</script>

<style lang="scss" scoped src="./FormPropertyInformation.scss"></style>
